import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { ExplanationAccordion } from 'components'
import { ImportBranchDto, ToDoListName, ToDoListsCountsDto } from 'dtos'

interface ToDoListItemProps {
  count?: number
  isLoading?: boolean
  label: string
  onClick: () => void
  selected?: boolean
}

export function ToDoListItem({ count, isLoading, label, onClick, selected }: ToDoListItemProps) {
  return (
    <Button size='small' color='primary' onClick={selected ? () => {} : onClick} variant={selected ? 'outlined' : 'text'} fullWidth>
      {label} ({isLoading ? <CircularProgress size={16} /> : count ?? 0})
    </Button>
  )
}

interface ToDoListFilterDialogProps {
  importBranch: ImportBranchDto | null
  importBranches: ImportBranchDto[]
  isGettingToDoListCounts: boolean
  onChangeImportBranch: (importBranch: ImportBranchDto | null) => void
  onChangeTodoList: (todoListName: ToDoListName) => void
  onClose: () => void
  open: boolean
  toDoListCounts: ToDoListsCountsDto
  toDoListName: ToDoListName
}

export default function ToDoListFilterDialog({
  importBranch,
  importBranches,
  isGettingToDoListCounts,
  onChangeImportBranch,
  onChangeTodoList,
  onClose,
  open,
  toDoListCounts,
  toDoListName
}: ToDoListFilterDialogProps) {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Dialog open={open} fullScreen={fullScreen}>
      <DialogContent>
        <DialogTitle>Select To Do List</DialogTitle>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Pre-Importation'
                titleTypographyProps={{ variant: 'h6', style: { color: 'white' } }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />

              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.waitingOnExporter}
                      isLoading={isGettingToDoListCounts}
                      label='Waiting on Exporter'
                      onClick={() => {
                        onChangeTodoList('waiting-on-exporter')
                        onClose()
                      }}
                      selected={toDoListName === 'waiting-on-exporter'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsSpecialBond}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Special Bond'
                      onClick={() => {
                        onChangeTodoList('needs-special-bond')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-special-bond'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsManufacturerLabelImageExporter}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Manufacturer Label Image (Exporter)'
                      onClick={() => {
                        onChangeTodoList('needs-manufacturer-label-image-exporter')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-manufacturer-label-image-exporter'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsTPMSLabelImageExporterPerAppendixB}
                      isLoading={isGettingToDoListCounts}
                      label='Needs TPMS Telltale Image (Exporter) per Appendix B'
                      onClick={() => {
                        onChangeTodoList('needs-tmps-label-image-exporter')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-tmps-label-image-exporter'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsBWIApproval}
                      isLoading={isGettingToDoListCounts}
                      label='Needs BWI Approval'
                      onClick={() => {
                        onChangeTodoList('needs-bwi-approval')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-bwi-approval'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeAddedToAPAPS}
                      isLoading={isGettingToDoListCounts}
                      label='Needs to be Added to a PAPS'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-added-to-a-paps')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-to-be-added-to-a-paps'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.cancelled}
                      isLoading={isGettingToDoListCounts}
                      label='Cancelled'
                      onClick={() => {
                        onChangeTodoList('cancelled')
                        onClose()
                      }}
                      selected={toDoListName === 'cancelled'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardContent>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Import Branch'
                      select
                      value={JSON.stringify(importBranch)}
                      onChange={e => {
                        onChangeImportBranch(JSON.parse(e.target.value) as ImportBranchDto | null)
                      }}
                    >
                      <MenuItem value={JSON.stringify(null)}>All Locations</MenuItem>

                      {importBranches.map(importBranch => (
                        <MenuItem key={importBranch.importBranchGuid} value={JSON.stringify(importBranch)}>
                          {importBranch.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Importation' // Vehicle Statuses
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />

              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsBrokerDocuments}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Broker Documents'
                      onClick={() => {
                        onChangeTodoList('needs-broker-documents')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-broker-documents'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsSignedForeignShipper}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Signed Foreign Shipper'
                      onClick={() => {
                        onChangeTodoList('needs-signed-foreign-shipper')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-signed-foreign-shipper'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.waitingOnTrip}
                      isLoading={isGettingToDoListCounts}
                      label='Waiting on Trip'
                      onClick={() => {
                        onChangeTodoList('waiting-on-trip')
                        onClose()
                      }}
                      selected={toDoListName === 'waiting-on-trip'}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsCheckIn}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Check-In'
                      onClick={() => {
                        onChangeTodoList('needs-check-in')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-check-in'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsManufacturerLabel}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Manufacturers Label'
                      onClick={() => {
                        onChangeTodoList('needs-manufacturer-label')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-manufacturer-label'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsTirePressureLabel}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Tire Pressure Label'
                      onClick={() => {
                        onChangeTodoList('needs-tire-pressure-label')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-tire-pressure-label'}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsPhotos}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Photos'
                      onClick={() => {
                        onChangeTodoList('needs-photos')
                        onClose()
                      }}
                      selected={toDoListName === 'needs-photos'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Recall'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsOEMSiteCheckedForRecalls}
                      isLoading={isGettingToDoListCounts}
                      label='Needs OEM Site Checked for Recalls'
                      onClick={() => {
                        onChangeTodoList('needs-oem-site-checked-for-recall')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsCAROUploaded}
                      isLoading={isGettingToDoListCounts}
                      label='Needs CA RO Uploaded'
                      onClick={() => {
                        onChangeTodoList('needs-ca-ro-uploaded')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsRecallWork}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Recall Work'
                      onClick={() => {
                        onChangeTodoList('needs-recall-work')
                        onClose()
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <Button
                      size='small'
                      color='primary'
                      onClick={() => {
                        onChangeTodoList('needs-recall-docs')
                        onClose()
                      }}
                      variant='text'
                    >
                      Needs Recall Docs (0)
                    </Button>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Cluster'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterRemoved}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Removed'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-removed')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterConversionShipper}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Conversion Shipper'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-conversion-shipper')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterDeliveredToThirdPartyConversionCompany}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Delivered To 3RD Party Conversion Company'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-delivered-to-third-party-conversion-company')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.waitingOnThirdPartyClusterConversion}
                      isLoading={isGettingToDoListCounts}
                      label='Waiting On 3RD Party Cluster Conversion'
                      onClick={() => {
                        onChangeTodoList('waiting-on-third-party-cluster-conversion')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterConversionInHouse}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Conversion In-House'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-conversion-in-house')
                        onClose()
                      }}
                    />
                  </Grid>
                  {/* Connor - 12/24/2024 Commenting out ToDoListSelections that do not appear in the current R20 specs. These may be utilized in the future. */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterConversionDetermination}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Conversion Determination'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-conversion-determination')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsInHouseConversion}
                      isLoading={isGettingToDoListCounts}
                      label='Needs In-House Conversion'
                      onClick={() => {
                        onChangeTodoList('needs-in-house-conversion')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.inConversion}
                      isLoading={isGettingToDoListCounts}
                      label='In Conversion'
                      onClick={() => {
                        onChangeTodoList('in-conversion')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterReinstalledAfterInHouseConversion}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Reinstalled After In-House Conversion'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-reinstalled-after-in-house-conversion')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterReinstalledAfterThirdPartyConversion}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Reinstalled After 3rd Party Conversion'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-reinstalled-after-3rd-party-conversion')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterInstallCompleted}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Install Completed'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-install-completed')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterProgrammingDetermination}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Programming Determination'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-programming-determination')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      Needs Shipper (Programming) (COMING SOON!)
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      Waiting on Programming (COMING SOON!)
                    </Button>
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsInHouseClusterProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Needs In-House Cluster Programming'
                      onClick={() => {
                        onChangeTodoList('needs-in-house-cluster-programming')
                        onClose()
                      }}
                    />
                  </Grid> */}

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsInHouseAdditionalProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Needs In-House Additional Programming'
                      onClick={() => {
                        onChangeTodoList('needs-in-house-additional-programming')
                        onClose()
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.inInHouseProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='In In-House Programming'
                      onClick={() => {
                        onChangeTodoList('in-in-house-programming')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsThirdPartyClusterProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Needs 3rd Party Cluster Programming'
                      onClick={() => {
                        onChangeTodoList('needs-3rd-party-cluster-programming')
                        onClose()
                      }}
                    />
                  </Grid> */}

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsThirdPartyAdditionalProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Needs 3rd Party Additional Programming'
                      onClick={() => {
                        onChangeTodoList('needs-3rd-party-additional-programming')
                        onClose()
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.waitingOnThirdPartyAdditionalProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Waiting On 3rd Party Additional Programming'
                      onClick={() => {
                        onChangeTodoList('waiting-on-3rd-party-additional-programming')
                        onClose()
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterReinstalled}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Reinstalled'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-reinstalled')
                        onClose()
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterReinstalledAfterInHouseProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Reinstalled After In-House Programming'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-reinstalled-after-in-house-programming')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsClusterReinstalledAfterThirdPartyProgramming}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Cluster Reinstalled After 3rd Party Programming'
                      onClick={() => {
                        onChangeTodoList('needs-cluster-reinstalled-after-3rd-party-programming')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsIncorrectMileageResolution}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Incorrect Mileage Resolution'
                      onClick={() => {
                        onChangeTodoList('needs-incorrect-mileage-resolution')
                        onClose()
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Michigan Titles'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needs7501DocumentMichigan}
                      isLoading={isGettingToDoListCounts}
                      label='Needs 7501 Document'
                      onClick={() => {
                        onChangeTodoList('needs-7501-document-michigan')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsCANOwnershipMichigan}
                      isLoading={isGettingToDoListCounts}
                      label='Needs CAN Ownership'
                      onClick={() => {
                        onChangeTodoList('needs-can-ownership-michigan')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsInfoForApplicationPacketMichigan}
                      isLoading={isGettingToDoListCounts}
                      label='Needs info for application packet'
                      onClick={() => {
                        onChangeTodoList('needs-info-for-application-packet-michigan')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBePrintedAndSigned}
                      isLoading={isGettingToDoListCounts}
                      label='Needs to be printed and signed'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-printed-and-signed')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeTakenToSecretaryOfState}
                      isLoading={isGettingToDoListCounts}
                      label='Needs to be taken to Secretary of State'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-taken-to-secretary-of-state')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsMichiganTitleApplicationReceipt}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Michigan Title Application Receipt'
                      onClick={() => {
                        onChangeTodoList('needs-michigan-title-application-receipt')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsMichiganTitleFromSecretaryOfState}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Michigan Title From Secretary of State'
                      onClick={() => {
                        onChangeTodoList('needs-michigan-title-from-secretary-of-state')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.michiganTitlesNeedsToBeSentOutgoing}
                      isLoading={isGettingToDoListCounts}
                      label='Michigan Titles Needs to be sent Outgoing'
                      onClick={() => {
                        onChangeTodoList('michigan-titles-needs-to-be-sent-outgoing')
                        onClose()
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Ohio Titles'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needs7501DocumentOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Needs 7501 Document'
                      onClick={() => {
                        onChangeTodoList('needs-7501-document-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsCANOwnershipOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Needs CAN Ownership'
                      onClick={() => {
                        onChangeTodoList('needs-can-ownership-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsInfoForApplicationPacketOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Needs info for application packet'
                      onClick={() => {
                        onChangeTodoList('needs-info-for-application-packet-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeMarkedReadyForBuildABox}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs to be Marked ready for build a Box'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-marked-ready-for-build-a-box')
                        onClose()
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeAddedToABundle}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs to be added to a bundle'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-added-to-a-bundle')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToHaveBoxFinished}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs to have box finished'
                      onClick={() => {
                        onChangeTodoList('needs-to-have-box-finished')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeTakenToFedEx}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs to be taken to FedEx'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-taken-to-fed-ex')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.waitingOnOhioTitleProcessing}
                      isLoading={isGettingToDoListCounts}
                      label='Waiting On Ohio Title Processing'
                      onClick={() => {
                        onChangeTodoList('waiting-on-ohio-title-processing')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsNewTitlesOCRd}
                      isLoading={isGettingToDoListCounts}
                      label={` Needs New Titles OCR'd`}
                      onClick={() => {
                        onChangeTodoList('needs-new-titles-ocrd')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeReadyForBox}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs to be ready for Box'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-ready-for-box')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeReadyForShipToOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Needs to be ready for Ship to Ohio'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-ready-for-ship-to-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBePutInABox}
                      isLoading={isGettingToDoListCounts}
                      label='Needs to be put in a box'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-put-in-a-box')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.readyToSendToOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Ready to send to Ohio'
                      onClick={() => {
                        onChangeTodoList('ready-to-send-to-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.shippedToOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Shipped to Ohio'
                      onClick={() => {
                        onChangeTodoList('shipped-to-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.receivedInOhio}
                      isLoading={isGettingToDoListCounts}
                      label='Received in Ohio'
                      onClick={() => {
                        onChangeTodoList('received-in-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.inProcessInOhio}
                      isLoading={isGettingToDoListCounts}
                      label='In process in Ohio'
                      onClick={() => {
                        onChangeTodoList('in-process-in-ohio')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.ohioTitlesNeedsToBeSentOutgoing}
                      isLoading={isGettingToDoListCounts}
                      label='Ohio Titles Needs to be sent Outgoing'
                      onClick={() => {
                        onChangeTodoList('ohio-titles-needs-to-be-sent-outgoing')
                        onClose()
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* <Grid item xs={12}>
            <Card>
              <CardHeader
                title='Titles'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                style={{ backgroundColor: '#2196F3', height: '30px' }}
              />

              <CardContent>
                <Grid container spacing={0}>
                  <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      WMAA (COMING SOON!)
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      Outgoing Titles (COMING SOON!)
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      Missouri Titles (COMING SOON!)
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid> */}

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Conformity'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.vehiclesEligibleForConformity}
                      isLoading={isGettingToDoListCounts}
                      label=' Vehicles Eligible For Conformity'
                      onClick={() => {
                        onChangeTodoList('vehicles-eligible-for-conformity')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.approaching120DaysSinceEntryDate}
                      isLoading={isGettingToDoListCounts}
                      label=' Approaching 120 Days Since Entry Date'
                      onClick={() => {
                        onChangeTodoList('approaching-120-days-since-entry-date')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.sendBackToCanada}
                      isLoading={isGettingToDoListCounts}
                      label=' Send Back To Canada'
                      onClick={() => {
                        onChangeTodoList('send-back-to-canada')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsTripEntryInfo}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Trip Entry Info'
                      onClick={() => {
                        onChangeTodoList('needs-trip-entry-info')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsConformityPhotos}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Conformity Photos'
                      onClick={() => {
                        onChangeTodoList('needs-conformity-photos')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsOEMSiteDoublecheck}
                      isLoading={isGettingToDoListCounts}
                      label='Needs OEM site Doublecheck'
                      onClick={() => {
                        onChangeTodoList('needs-oem-site-doublecheck')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsConformitySentToDOT}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Conformity sent to DOT'
                      onClick={() => {
                        onChangeTodoList('needs-conformity-sent-to-dot')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.conformitySentToDOT}
                      isLoading={isGettingToDoListCounts}
                      label='Conformity Sent to DOT (Hold)'
                      onClick={() => {
                        onChangeTodoList('conformity-sent-to-dot-(hold)')
                        onClose()
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Outbound Trips'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.readyForOutboundTrip}
                      isLoading={isGettingToDoListCounts}
                      label='Ready for Outbound Trip'
                      onClick={() => {
                        onChangeTodoList('ready-for-outbound-trip')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsToBeMovedToStaging}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs to be moved to Staging'
                      onClick={() => {
                        onChangeTodoList('needs-to-be-moved-to-staging')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsDriversLicensePhotoUploaded}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs Drivers License Photo Uploaded'
                      onClick={() => {
                        onChangeTodoList('needs-drivers-license-photo-uploaded')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsSignedBOL}
                      isLoading={isGettingToDoListCounts}
                      label=' Needs Signed BOL'
                      onClick={() => {
                        onChangeTodoList('needs-signed-bol')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsOutboundPhotos}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Outbound Photos'
                      onClick={() => {
                        onChangeTodoList('needs-outbound-photos')
                        onClose()
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      Needs BOL In Vehicle (COMING SOON!)
                    </Button>
                  </Grid> */}
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsRecallWarrantyInGlovebox}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Recall Warranty in Glovebox'
                      onClick={() => {
                        onChangeTodoList('needs-recall-warranty-in-glovebox')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsOutboundTransport}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Outbound Transport'
                      onClick={() => {
                        onChangeTodoList('needs-outbound-transport')
                        onClose()
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Invoicing'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needToInvoice}
                      isLoading={isGettingToDoListCounts}
                      label='Needs to Invoice'
                      onClick={() => {
                        onChangeTodoList('need-to-invoice')
                        onClose()
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant='outlined'>
              <CardHeader
                title='Other'
                titleTypographyProps={{
                  variant: 'h6',
                  style: { color: 'white' }
                }}
                sx={{ backgroundColor: '#2196F3', height: '30px' }}
              />
              <CardContent>
                <Grid container>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.needsDamageRepaired}
                      isLoading={isGettingToDoListCounts}
                      label='Needs Damage Repaired'
                      onClick={() => {
                        onChangeTodoList('needs-damage-repaired')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.created5DaysAgoOrMoreAndHasNotBeenSentToDOT}
                      isLoading={isGettingToDoListCounts}
                      label='Created 5 days ago or more and has not been sent to DOT'
                      onClick={() => {
                        onChangeTodoList('created-5-days-ago-or-more-and-has-not-been-sent-to-dot')
                        onClose()
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ToDoListItem
                      count={toDoListCounts?.archived}
                      isLoading={isGettingToDoListCounts}
                      label='Archived'
                      onClick={() => {
                        onChangeTodoList('archived')
                        onClose()
                      }}
                    />
                  </Grid>

                  {/* <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      Vehicles On Lot (COMING SOON!)
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Button size='small' color='primary' onClick={onClose} variant='text'>
                      No CAN Ownership (COMING SOON!)
                    </Button>
                  </Grid> */}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <Grid item xs={12} sx={{ mx: 3 }}>
        <ExplanationAccordion variant='outlined'>
          Import Branch dropdown lists these options: All Locations, HQ Location, Flatrock Location, and Columbus Location. branches. || If additional branches
          are ever added, a code update will be required. || The number in parenthesis represents the number of vehicles found matching that status, etc. || For
          the “Pre-Importation” to do lists, the vehicles in these to do lists are not associated with any Import Branch (vehicles become associated with an
          Import Branch once the vehicle is added to an Inbound Trip). Because of this, the “Pre-Importation” to do lists ignore the “Import Branch” filter and
          act like the “All Locations” options is selected. ||
        </ExplanationAccordion>
      </Grid>

      <DialogActions sx={{ mb: 2, mr: 2 }}>
        <Button color='inherit' onClick={onClose} variant='text'>
          CANCEL
        </Button>
      </DialogActions>
    </Dialog>
  )
}
